class PredictiveSearch extends HTMLElement {
    constructor() {
        super();

        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('#predictive-search');
        this.searchRoot = this.closest('.ocsearch.search');

        if (!this.input || !this.predictiveSearchResults) {
            return;
        }

        this.input.dataset.predictiveLastTerm = '';
        this.input.dataset.predictiveHasResults = 'true';
        this.input.dataset.predictiveFallbackTerm = '';
        this.input.dataset.predictiveResolvedTerm = '';
        this.requestToken = 0;

        this.input.addEventListener('input', this.debounce((event) => {
            this.onChange(event);
        }, 300).bind(this));
    }

    onChange() {
        const searchTerm = this.input.value.trim();
        const currentToken = ++this.requestToken;

        if (!searchTerm.length) {
            this.setLoading(false);
            this.close();
            return;
        }

        this.setLoading(true);
        this.close();
        this.getSearchResults(searchTerm, currentToken);
    }

    getSearchResults(searchTerm, requestToken) {
        const variants = this.getSearchTermVariants(searchTerm);
        let firstMarkup = '';
        let fallbackTerm = variants.length > 1 ? variants[1] : '';

        const tryVariant = (variantIndex) => {
            if (requestToken !== this.requestToken) {
                return;
            }

            if (variantIndex >= variants.length) {
                this.input.dataset.predictiveLastTerm = searchTerm;
                this.input.dataset.predictiveHasResults = 'false';
                this.input.dataset.predictiveFallbackTerm = fallbackTerm;
                this.input.dataset.predictiveResolvedTerm = '';
                this.setLoading(false);

                if (firstMarkup) {
                    this.predictiveSearchResults.innerHTML = firstMarkup;
                    this.open();
                    return;
                }

                this.close();
                return;
            }

            this.fetchSearchResults(variants[variantIndex])
                .then((text) => {
                    if (requestToken !== this.requestToken) {
                        return;
                    }

                    const shouldFilterByOriginal = variants[variantIndex] !== searchTerm;
                    const parsed = this.parseResultsMarkup(
                        text,
                        shouldFilterByOriginal ? this.normalizeSearchTerm(searchTerm) : ''
                    );

                    if (!firstMarkup && parsed.markup) {
                        firstMarkup = parsed.markup;
                    }

                    if (parsed.hasResults) {
                        this.input.dataset.predictiveLastTerm = searchTerm;
                        this.input.dataset.predictiveHasResults = 'true';
                        this.input.dataset.predictiveFallbackTerm = variants[variantIndex] !== searchTerm ? variants[variantIndex] : fallbackTerm;
                        this.input.dataset.predictiveResolvedTerm = variants[variantIndex];
                        this.setLoading(false);

                        this.predictiveSearchResults.innerHTML = parsed.markup;
                        this.open();
                        return;
                    }

                    tryVariant(variantIndex + 1);
                })
                .catch((error) => {
                    this.setLoading(false);
                    this.close();
                    throw error;
                });
        };

        tryVariant(0);
    }

    fetchSearchResults(searchTerm) {
        const encodedSearchTerm = encodeURIComponent(searchTerm);
        const routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        const predictiveSearchUrl = `${routesRoot}search/suggest`;

        return fetch(`${predictiveSearchUrl}?q=${encodedSearchTerm}&resources[type]=product,page,article&resources[limit]=4&resources[limit_scope]=each&section_id=predictive-search`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(response.status);
                }

                return response.text();
            });
    }

    parseResultsMarkup(text, normalizedFilterTerm = '') {
        const section = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search');

        if (!section) {
            return { markup: '', hasResults: false };
        }

        let markup = section.innerHTML;

        if (normalizedFilterTerm) {
            markup = this.filterResultsMarkupByNormalizedTerm(markup, normalizedFilterTerm);
        }

        const probe = document.createElement('div');
        probe.innerHTML = markup;

        const hasProducts = !!probe.querySelector('#predictive-search-results-products-list .predictive-search__list-item');
        const hasGuides = !!probe.querySelector('#predictive-search-results-pages-list-mobile .predictive-search__list-item');
        const hasPages = !!probe.querySelector('#predictive-search-results-pages-list-desktop .predictive-search__list-item');

        return { markup, hasResults: hasProducts || hasGuides || hasPages };
    }

    filterResultsMarkupByNormalizedTerm(markup, normalizedTerm) {
        if (!normalizedTerm) {
            return markup;
        }

        const probe = document.createElement('div');
        probe.innerHTML = markup;
        const listItems = probe.querySelectorAll('.predictive-search__list-item');

        listItems.forEach((item) => {
            const heading = item.querySelector('.predictive-search__item-heading');
            const headingText = heading ? heading.textContent.trim() : '';
            const normalizedHeading = this.normalizeSearchTerm(headingText);

            if (!normalizedHeading || !normalizedHeading.includes(normalizedTerm)) {
                item.remove();
            }
        });

        return probe.innerHTML;
    }

    getSearchTermVariants(searchTerm) {
        const baseTerm = searchTerm.trim();
        const variants = [baseTerm];

        if (!baseTerm || baseTerm.includes(' ')) {
            return variants;
        }

        const normalized = this.normalizeSearchTerm(baseTerm);

        if (!normalized) {
            return variants;
        }

        const candidates = [];

        if (/^[a-z0-9]{2,5}$/.test(normalized)) {
            candidates.push(normalized.split('').join(' '));
            candidates.push(normalized.split('').join('.'));
            candidates.push(`${normalized.split('').join('.')}.`);
        }

        if (normalized.length >= 6) {
            candidates.push(`${normalized.slice(0, 3)} ${normalized.slice(3)}`);
        }

        if (normalized.length >= 7) {
            candidates.push(`${normalized.slice(0, 4)} ${normalized.slice(4)}`);
            candidates.push(`${normalized.slice(0, 3)}'${normalized.slice(3)}`);
        }

        if (normalized.length >= 4) {
            candidates.push(normalized.slice(0, 3));
        }

        candidates.forEach((candidate) => {
            if (!candidate) {
                return;
            }

            if (!variants.some((variant) => variant.toLowerCase() === candidate.toLowerCase())) {
                variants.push(candidate);
            }
        });

        return variants;
    }

    normalizeSearchTerm(term) {
        return term
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]/g, '');
    }

    open() {
        if (this.searchRoot) {
            this.searchRoot.classList.remove('search--loading');
            this.searchRoot.classList.add('search--loaded');
        }

        this.predictiveSearchResults.style.display = 'block';
        this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
        if (this.searchRoot) {
            this.searchRoot.classList.remove('search--loaded');
        }

        this.predictiveSearchResults.style.display = 'none';
        this.input.setAttribute('aria-expanded', 'false');
    }

    setLoading(isLoading) {
        this.classList.toggle('is-loading', Boolean(isLoading));

        if (this.searchRoot) {
            this.searchRoot.classList.toggle('search--loading', Boolean(isLoading));
        }
    }

    debounce(fn, wait) {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn.apply(this, args), wait);
        };
    }
}

customElements.define('predictive-search', PredictiveSearch);

const predictiveSearchInput = document.querySelector('.predictive-search-input');
const searchToggle = document.getElementById('SearchToggle');
const predictiveSearchForm = predictiveSearchInput ? predictiveSearchInput.closest('form.search__form') : null;

function refreshPredictiveInput() {
    if (!predictiveSearchInput) {
        return;
    }

    const inputEvent = new Event('input', { bubbles: true });
    predictiveSearchInput.dispatchEvent(inputEvent);
}

function applyPredictiveFallbackTerm() {
    if (!predictiveSearchInput) {
        return;
    }

    const inputValue = predictiveSearchInput.value.trim();
    const lastTerm = predictiveSearchInput.dataset.predictiveLastTerm || '';
    const hasResults = predictiveSearchInput.dataset.predictiveHasResults === 'true';
    const fallbackTerm = predictiveSearchInput.dataset.predictiveFallbackTerm || '';
    const resolvedTerm = predictiveSearchInput.dataset.predictiveResolvedTerm || '';
    const normalizedLastTerm = normalizeClientSearchTerm(lastTerm);

    if (
        resolvedTerm &&
        resolvedTerm.length <= 3 &&
        normalizedLastTerm.length >= 4 &&
        inputValue === lastTerm
    ) {
        predictiveSearchInput.value = `${normalizedLastTerm.slice(0, 3)} ${normalizedLastTerm.slice(3)}`;
        return;
    }

    if (resolvedTerm && resolvedTerm !== lastTerm && inputValue === lastTerm) {
        predictiveSearchInput.value = resolvedTerm;
        return;
    }

    if (!hasResults && inputValue && inputValue === lastTerm && fallbackTerm) {
        predictiveSearchInput.value = fallbackTerm;
    }
}

if (searchToggle && predictiveSearchInput) {
    searchToggle.addEventListener('click', () => {
        window.setTimeout(() => {
            predictiveSearchInput.focus();
        }, 0);
    });
}

document.addEventListener('click', (event) => {
    const clearButton = event.target.closest('.search-clear-button');

    if (clearButton) {
        event.preventDefault();

        if (predictiveSearchInput) {
            predictiveSearchInput.value = '';
        }

        refreshPredictiveInput();
        return;
    }

    const submitButton = event.target.closest('[data-predictive-search-submit]');

    if (!submitButton) {
        return;
    }

    const searchForm = submitButton.closest('form.search__form');

    if (!searchForm) {
        return;
    }

    event.preventDefault();

    applyPredictiveFallbackTerm();

    if (typeof searchForm.requestSubmit === 'function') {
        searchForm.requestSubmit();
        return;
    }

    searchForm.submit();
});

if (predictiveSearchForm) {
    predictiveSearchForm.addEventListener('submit', () => {
        applyPredictiveFallbackTerm();
    });
}

function normalizeClientSearchTerm(term) {
    return term
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
}
